import React from 'react';

import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { marked } from 'marked';

import { SubfactReference } from './SubfactReference';
import { TranscriptSubfact } from '../../../../api/types/backend-dto-types';

interface TextWithSubfactReferencesProps {
  text: string;
  caseId?: string;
  transcriptId?: string;
  onBlockClick?: (blockId: string) => void;
  className?: string;
}

// Regex to match references in format: [n]{subfact-id}
const SUBFACT_REFERENCE_REGEX = /\[(\d+)\]\{([^}]+)\}/g;

export const TextWithSubfactReferences: React.FC<TextWithSubfactReferencesProps> = ({
  text,
  caseId,
  transcriptId,
  onBlockClick,
  className = '',
}) => {
  if (!text) {
    return null;
  }

  // First, parse the markdown to HTML
  const htmlContent = marked(text) as string;

  // Then use html-react-parser to replace references with React components
  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      // Find text nodes that might contain references
      if (domNode.type === 'text') {
        const textContent = domNode.data;

        // Check if this text contains any references
        const hasReferences = SUBFACT_REFERENCE_REGEX.test(textContent);
        if (!hasReferences) {
          return domNode;
        }

        // Reset regex lastIndex
        SUBFACT_REFERENCE_REGEX.lastIndex = 0;

        // Build array of text parts and reference components
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = SUBFACT_REFERENCE_REGEX.exec(textContent)) !== null) {
          // Add text before the reference
          if (match.index > lastIndex) {
            parts.push(textContent.slice(lastIndex, match.index));
          }

          // Add the reference component
          const referenceNumber = parseInt(match[1], 10);
          const subfactId = match[2];

          parts.push(
            <SubfactReference
              key={`ref-${match.index}-${subfactId}`}
              referenceNumber={referenceNumber}
              subfactId={subfactId}
              caseId={caseId}
              transcriptId={transcriptId}
              onBlockClick={onBlockClick}
            />,
          );

          lastIndex = match.index + match[0].length;
        }

        // Add any remaining text
        if (lastIndex < textContent.length) {
          parts.push(textContent.slice(lastIndex));
        }

        // Return the parts wrapped in a fragment
        return <>{parts}</>;
      }

      return domNode;
    },
  };

  // Parse the HTML with our custom replacer
  const parsedContent = parse(htmlContent, options);

  return <div className={`prose prose-sm max-w-none ${className}`}>{parsedContent}</div>;
};

TextWithSubfactReferences.displayName = 'TextWithSubfactReferences';
